<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :header-height="50"
            :is-search="false"
            :is-auto-width="true"
            :column-defs="columnDefs"
            :statusBar="statusBar"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :range-months-limit="12"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div class="d-none d-md-flex align-items-baseline">
                <responsive-dropdown
                    :items="failureStatusOptions"
                    :icon="['fas', 'filter']"
                    :responsiveness="false"
                    variant="primary"
                >
                    {{ failureStatusLabel }}
                </responsive-dropdown>
            </div>
            <fa-icon-tooltip id="icon-info" :icon="['fas', 'circle-info']" class="text-primary h-100" variant="info">
                <span style="text-align: left">
                    We will notify you and all capable of processing payment team members with a modal as long as
                    transaction is unassigned.
                </span>
                <span style="text-align: left">We will notify only you if transaction is assigned to you.</span>
            </fa-icon-tooltip>
        </grid>
        <modal
            :id="changeCurrentRowFailureStatusModalId"
            :title="failureStatusConfirmationModalTitle"
            @hidden="cancelCurrentRowFailureStatusChange"
            @ok="changeCurrentRowFailureStatus"
            ok-title="Confirm"
        >
            <busy-modal :show="busy"></busy-modal>
            <p>{{ failureStatusConfirmationModalText }}</p>
        </modal>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../../api';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FaIconTooltip from '../../generic/fa-icon-tooltip/fa-icon-tooltip.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import ShowErrorsCellRenderer from './show-errors-cell-renderer.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        TOMORROW,
        THIS_WEEK,
        NEXT_WEEK,
        NEXT_MONTH,
        THIS_YEAR,
        LAST_YEAR,
        LAST_WEEK,
        ALL_DATES,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import {
        REPORT_FAILED_TRANSACTIONS_ASSIGNEE_ENDPOINT,
        REPORT_FAILED_TRANSACTIONS_ASSIGNEE_OPTIONS_ENDPOINT,
        REPORT_FAILED_TRANSACTIONS_ENDPOINT,
        REPORT_FAILED_TRANSACTIONS_UNRESOLVED_ENDPOINT,
    } from '../../../endpoints';
    import ReportSummaryBar from '../report-summary-bar.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import { formatResponseError } from '../../../utils/api';
    import { isMobileScreen } from '../../../utils/screens';

    export default {
        name: 'ReportFailedTransactions',
        components: {
            Page,
            Grid,
            Modal,
            BusyModal,
            FaIconTooltip,
            DateRangePicker,
            reportSummaryBar: ReportSummaryBar,
            ResponsiveDropdown,
            showErrorsCellRenderer: ShowErrorsCellRenderer,
        },
        mixins: [removeScrollMixin],
        data() {
            const failureStatus = this.$store.getters['failedTransactionsLocalStorage/failureStatus'];
            return {
                title: 'Failed Transactions',
                tabs: [{ title: 'Failed Transactions', route: '/app/reports/failed-transactions/', active: true }],
                dateRange: nameToRange(ALL_DATES),
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    ALL_DATES,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
                busy: false,
                gridApi: null,
                rowData: [],
                getRowStyle: null,
                pinnedBottomRowData: null,
                previousRowFailureStatus: null,
                currentRowFailureStatus: null,
                changeFailureStatusClicked: false,
                windowWidth: 0,
                windowHeight: 0,
                statusBar: null,
                payment_methods: {},
                totalRecords: 0,
                failureStatus: failureStatus,
                changeCurrentRowFailureStatusModalId: 'change-current-row-failure-status-modal-id',
                assigneeOptions: [{ value: null, text: 'Not Selected' }],
                failureStatusCellOptions: [
                    { text: 'Resolved', value: 'resolved' },
                    { text: 'Unresolved', value: 'unresolved' },
                    { text: 'Undergoing Resolution', value: 'undergoing_resolution' },
                ],
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            columnDefs() {
                return [
                    {
                        field: 'index',
                        headerName: '#',
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                        },
                        minWidth: 65,
                    },
                    {
                        field: 'date',
                        headerName: 'Date',
                        minWidth: 100,
                        sort: 'desc',
                    },
                    {
                        field: 'guest_name',
                        headerName: 'Guest Name',
                        minWidth: 100,
                    },
                    {
                        field: 'invoice_id',
                        headerName: 'Invoice #',
                        minWidth: 100,
                        cellRenderer: ({ data, node }) => {
                            if (node.rowPinned) return '';
                            return `
                        <a 
                            target="_blank" 
                            href="/app/guest/${data.guest_id}/invoices/?reservation=${data.invoice_id}&tab=transactions"
                        >
                            ${data.invoice_id}
                        </a>`;
                        },
                    },
                    {
                        field: 'property',
                        headerName: 'Property',
                        minWidth: 110,
                    },
                    ...Object.keys(this.payment_methods).map((pm_key) => {
                        const pm = this.payment_methods[pm_key];
                        return {
                            field: pm_key,
                            headerName: pm,
                            type: 'amountColumn',
                            cellStyle: ({ data, node }) => {
                                const value = data[pm_key] || 0;
                                if (value < 0) return { color: 'red' };
                                return null;
                            },
                            minWidth: 124,
                        };
                    }),
                    {
                        field: 'total',
                        headerName: 'Total',
                        type: 'amountColumn',
                        cellStyle: ({ data }) => {
                            const { total } = data;
                            if (total < 0) return { color: 'red' };
                            return null;
                        },
                        minWidth: 100,
                        hide: Object.keys(this.payment_methods).length < 2,
                    },
                    {
                        field: 'processing_type',
                        headerName: 'Processing Type',
                        minWidth: 110,
                    },
                    {
                        field: 'sequence',
                        headerName: 'Sequence',
                        minWidth: 100,
                    },
                    {
                        field: 'transaction_code',
                        headerName: 'Transaction ID',
                        minWidth: 120,
                    },
                    {
                        field: 'is_payment_requested',
                        headerName: 'Payment Requested (Automatic)',
                        type: 'yesNoColumnNew',
                        cellRendererParams: {
                            valueGetter: ({ node, data }) =>
                                data && !node.rowPinned ? data.is_payment_requested : null,
                        },
                        minWidth: 120,
                        maxWidth: 120,
                    },
                    {
                        field: '',
                        headerName: 'Errors',
                        minWidth: 100,
                        cellRenderer: 'showErrorsCellRenderer',
                    },
                    {
                        field: 'resolution_assignee',
                        headerName: 'Assigned for Resolution',
                        cellEditor: 'selectCellEditor',
                        cellClass: ({ node }) => (!node.rowPinned ? 'lx-editable-cell text-primary' : ''),
                        editable: ({ node }) => !node.rowPinned,
                        autoWidthExclude: true,
                        resizable: false,
                        minWidth: 300,
                        cellRenderer: ({ node, data }) =>
                            data && !node.rowPinned ? this.getAssigneeName(data.resolution_assignee) : '',
                        cellEditorParams: () => ({ options: this.assigneeOptions }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.get(REPORT_FAILED_TRANSACTIONS_ASSIGNEE_ENDPOINT(data.id), {
                                        params: { resolution_assignee: newValue },
                                    });
                                    this.refresh();
                                } catch (error) {
                                    const { generic, fields } = formatResponseError(error);
                                    const errorText =
                                        fields['resolution_assignee'] && !!fields['resolution_assignee'].length
                                            ? fields['resolution_assignee'][0]
                                            : generic;
                                    this.errorToast({ text: errorText || `Could not change assignee.` });
                                    data.resolution_assignee = oldValue;
                                    node.setData(data);
                                    return;
                                }
                            })();
                        },
                    },
                    {
                        field: 'failure_status',
                        headerName: 'Failure Status',
                        cellClass: ({ node }) => (!node.rowPinned ? 'lx-editable-cell text-primary' : ''),
                        editable: ({ node }) => !node.rowPinned,
                        cellEditor: 'selectCellEditor',
                        minWidth: 200,
                        pinned: isMobileScreen() ? undefined : 'right',
                        cellRenderer: ({ data }) => data && this.getFailureStatusText(data.failure_status),
                        cellEditorParams: () => ({
                            onChange: (params, value) => {
                                this.currentRowData = params.data;
                                this.previousRowFailureStatus = this.currentRowData.failure_status;
                                this.currentRowFailureStatus = value;
                                this.$bvModal.show(this.changeCurrentRowFailureStatusModalId);
                            },
                            options: this.failureStatusCellOptions,
                        }),
                    },
                ];
            },
            failureStatusConfirmationModalTitle() {
                return `Mark Transaction as ${
                    this.currentRowFailureStatus == 'resolved'
                        ? 'Resolved'
                        : this.currentRowFailureStatus == 'unresolved'
                        ? 'Unresolved'
                        : 'Undergoing Resolution'
                }`;
            },
            failureStatusConfirmationModalText() {
                return `Please confirm this billing issue ${
                    this.currentRowFailureStatus == 'resolved'
                        ? 'has been resolved.'
                        : this.currentRowFailureStatus == 'unresolved'
                        ? 'is unresolved.'
                        : 'is undergoing resolution.'
                }`;
            },
            failureStatusOptions() {
                return [
                    {
                        label: 'Resolved',
                        active: this.failureStatus === 'resolved',
                        click: () => {
                            this.failureStatus = 'resolved';
                            this.refresh();
                            this.$store.dispatch('failedTransactionsLocalStorage/changeFailureStatus', 'resolved');
                        },
                    },
                    {
                        label: 'Unresolved',
                        active: this.failureStatus === 'unresolved',
                        click: () => {
                            this.failureStatus = 'unresolved';
                            this.refresh();
                            this.$store.dispatch('failedTransactionsLocalStorage/changeFailureStatus', 'unresolved');
                        },
                    },
                    {
                        label: 'Undergoing Resolution',
                        active: this.failureStatus === 'undergoing_resolution',
                        click: () => {
                            this.failureStatus = 'undergoing_resolution';
                            this.refresh();
                            this.$store.dispatch(
                                'failedTransactionsLocalStorage/changeFailureStatus',
                                'undergoing_resolution'
                            );
                        },
                    },
                ];
            },
            failureStatusLabel() {
                switch (this.failureStatus) {
                    case 'resolved':
                        return 'Resolved';
                    case 'unresolved':
                        return 'Unresolved';
                    case 'undergoing_resolution':
                        return 'Undergoing Resolution';
                }
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md || this.windowHeight < md;
            },
        },
        mounted() {
            Promise.all([this.fetchAssigneeOptions(), this.fetchData()]);
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            grid.gridOptions.enableBrowserTooltips = true;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) return { 'font-weight': 'bold' };
            };
            this.pinnedBottomRowData = [{ index: 0, total: 0 }];
            this.statusBar = {
                statusPanels: [{ key: 'reportSummaryBar', statusPanel: 'reportSummaryBar', align: 'left' }],
            };
        },
        methods: {
            refresh() {
                this.fetchData();
            },
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_FAILED_TRANSACTIONS_ENDPOINT('row-data'), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            failure_status: this.failureStatus,
                        },
                    });
                    const { transactions, total, payment_table, payment_methods } = data;
                    this.payment_methods = payment_methods;
                    this.rowData = [
                        ...transactions.map((transaction, index) => ({
                            id: index,
                            ...transaction,
                            ...transaction.payment_table,
                        })),
                    ];
                    this.totalRecords = this.rowData.length;
                    const statusBar = { index: this.rowData.length, total, ...payment_table };
                    this.pinnedBottomRowData = [{ ...statusBar }];
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAssigneeOptions() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_FAILED_TRANSACTIONS_ASSIGNEE_OPTIONS_ENDPOINT());
                    this.assigneeOptions = [{ value: null, text: 'Not Selected' }].concat(
                        data.map((a) => {
                            return { value: a.id, text: `${a.first_name} ${a.last_name} (${a.email})` };
                        })
                    );
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch assignee options.' });
                } finally {
                    this.busy = false;
                }
            },
            cancelCurrentRowFailureStatusChange() {
                if (!this.changeFailureStatusClicked) {
                    this.currentRowData.failure_status = this.previousRowFailureStatus;
                    this.gridApi.refreshCells();
                    this.changeFailureStatusClicked = false;
                }
            },
            async changeCurrentRowFailureStatus() {
                this.changeFailureStatusClicked = true;
                try {
                    await API.get(REPORT_FAILED_TRANSACTIONS_UNRESOLVED_ENDPOINT(this.currentRowData.id), {
                        params: { failure_status: this.currentRowFailureStatus },
                    });
                    this.refresh();
                } catch (error) {
                    this.changeFailureStatusClicked = false;
                    this.cancelCurrentRowFailureStatusChange();
                    const { generic, fields } = formatResponseError(error);
                    const errorText =
                        fields['failure_status'] && !!fields['failure_status'].length
                            ? fields['failure_status'][0]
                            : generic;
                    this.errorToast({
                        text:
                            errorText ||
                            `Could not change failure status to ${this.getFailureStatusText(
                                this.currentRowFailureStatus
                            )}.`,
                    });
                }
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
                const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
                let componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                componentInstance.setVisible(this.isMobile);
                componentInstance.setTitle(this.title);
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            getAssigneeName(assigneeId) {
                return this.assigneeOptions.find((a) => a.value == assigneeId)?.text || 'Not Selected';
            },
            getFailureStatusText(failureStatus) {
                switch (failureStatus) {
                    case 'resolved':
                        return 'Resolved';
                    case 'unresolved':
                        return 'Unresolved';
                    case 'undergoing_resolution':
                        return 'Undergoing Resolution';
                }
            },
        },
    };
</script>
